import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, Save, CheckCircle, XCircle, Edit, FileText,
  User, Calendar, Hash, FileCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChecklistDetail = () => {
  const navigate = useNavigate();
  const { sheetId } = useParams();
  const [sheet, setSheet] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [projects, setProjects] = useState([]);
  const [welders, setWelders] = useState([]);

  const loadSheet = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Load sheet
      const sheetResponse = await axios.get(`${API}/checklists/${sheetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSheet(sheetResponse.data);
      setFormData(sheetResponse.data.data || {});
      
      // Load template for this sheet type
      const templateResponse = await axios.get(
        `${API}/checklists/templates/${sheetResponse.data.sheet_type}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplate(templateResponse.data);
      
      // Load projects list
      const projectsResponse = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(projectsResponse.data || []);
      
      // Load welders (users with Staff, Manager, or Admin role)
      const usersResponse = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allUsers = usersResponse.data || [];
      const welderUsers = allUsers.filter(u => 
        ['Staff', 'Manager', 'Admin'].includes(u.role)
      );
      setWelders(welderUsers);
      
    } catch (error) {
      console.error('Failed to load Checklist:', error);
      toast.error('Failed to load Checklist');
      navigate('/tools/checklists');
    } finally {
      setLoading(false);
    }
  }, [sheetId, navigate]);

  useEffect(() => {
    loadSheet();
  }, [loadSheet]);

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Extract standard fields from formData
      const { project_id, welder_id, welder_name, inspection_date, ...customData } = formData;
      
      await axios.put(
        `${API}/checklists/${sheetId}`,
        { 
          project_id: project_id || sheet.project_id,
          data: {
            ...customData,
            welder_id,
            welder_name,
            inspection_date
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Checklist updated successfully');
      setEditing(false);
      loadSheet();
    } catch (error) {
      console.error('Failed to update sheet:', error);
      toast.error('Failed to update Checklist');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/checklists/${sheetId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${newStatus}`);
      loadSheet();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const getStatusBadgeVariant = (status) => {
    const variants = {
      draft: 'secondary',
      in_progress: 'default',
      under_review: 'default',
      approved: 'default',
      rejected: 'destructive'
    };
    return variants[status] || 'secondary';
  };

  if (loading) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Loading Checklist...
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!sheet) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Checklist not found</p>
            <Button className="mt-4" onClick={() => navigate('/tools/checklists')}>
              Back to Checklists
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-2">
            <Button
              onClick={() => navigate('/tools/checklists')}
              variant="outline"
              size="sm"
              className="font-semibold text-gray-900 border-gray-400"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Checklists
            </Button>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-black tracking-tight uppercase text-gray-900">{sheet.title}</h1>
            <Badge variant={getStatusBadgeVariant(sheet.status)} className="text-sm font-bold">
              {sheet.status.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
          <p className="text-gray-800 font-semibold text-base">
            {sheet.sheet_number} • Revision {sheet.revision}
          </p>
        </div>
        <div className="flex gap-2">
          {!editing && sheet.status !== 'approved' && (
            <Button onClick={() => setEditing(true)}>
              <Edit size={16} className="mr-2" />
              Edit
            </Button>
          )}
          {editing && (
            <>
              <Button variant="outline" onClick={() => {
                setEditing(false);
                setFormData(sheet.data || {});
              }}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                <Save size={16} className="mr-2" />
                Save Changes
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Sheet Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-gray-900">Sheet Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Sheet Type</Label>
                  <p className="font-medium mt-1 text-gray-900">{sheet.sheet_type}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Sheet Number</Label>
                  <p className="font-medium mt-1 text-gray-900">{sheet.sheet_number}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Revision</Label>
                  <p className="font-medium mt-1 text-gray-900">{sheet.revision}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    {sheet.status === 'approved' ? (
                      <Badge variant={getStatusBadgeVariant(sheet.status)}>
                        {sheet.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    ) : (
                      <Select value={sheet.status} onValueChange={handleStatusChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="under_review">Under Review</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              </div>

              {sheet.notes && (
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Notes</Label>
                  <p className="mt-1 text-gray-900">{sheet.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sheet Data - Dynamic based on sheet type */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-gray-900">Sheet Data</CardTitle>
                  <CardDescription className="text-gray-700">
                    {template && template.fields && template.fields.length > 0
                      ? 'Fill in the required information'
                      : 'No custom fields configured for this template'}
                  </CardDescription>
                </div>
                {template && template.fields && template.fields.length === 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/checklists/template-builder/${sheet.sheet_type}`)}
                  >
                    Configure Template
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Standard Fields - Always Shown */}
              <div className="space-y-6 pb-6 border-b-2 border-gray-300 mb-6">
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-4">
                  <p className="text-sm font-semibold text-gray-900">Standard Information</p>
                  <p className="text-xs text-gray-700 mt-1">These fields are required for all Checklists</p>
                </div>

                {/* Project Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">
                    Project *
                  </Label>
                  {editing ? (
                    <Select 
                      value={formData.project_id || sheet.project_id || ''} 
                      onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                    >
                      <SelectTrigger className="border-gray-300 text-gray-900">
                        <SelectValue placeholder="Select a project..." />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.project_name || project.customer_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-gray-800 bg-gray-50 p-2 rounded border">
                      {projects.find(p => p.id === (formData.project_id || sheet.project_id))?.project_name || 
                       projects.find(p => p.id === (formData.project_id || sheet.project_id))?.customer_name || 
                       'Not assigned'}
                    </p>
                  )}
                </div>

                {/* Welder Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">
                    Welder/Inspector *
                  </Label>
                  {editing ? (
                    <Select 
                      value={formData.welder_id || ''} 
                      onValueChange={(value) => {
                        const welder = welders.find(w => w.id === value);
                        setFormData({ 
                          ...formData, 
                          welder_id: value,
                          welder_name: welder?.name || ''
                        });
                      }}
                    >
                      <SelectTrigger className="border-gray-300 text-gray-900">
                        <SelectValue placeholder="Select welder/inspector..." />
                      </SelectTrigger>
                      <SelectContent>
                        {welders.map((welder) => (
                          <SelectItem key={welder.id} value={welder.id}>
                            {welder.name} ({welder.role})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-gray-800 bg-gray-50 p-2 rounded border">
                      {formData.welder_name || 'Not assigned'}
                    </p>
                  )}
                </div>

                {/* Inspection Date/Time */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">
                    Inspection Date & Time *
                  </Label>
                  {editing ? (
                    <Input
                      type="datetime-local"
                      value={formData.inspection_date || ''}
                      onChange={(e) => setFormData({ ...formData, inspection_date: e.target.value })}
                      className="border-gray-300 text-gray-900"
                    />
                  ) : (
                    <p className="text-sm text-gray-800 bg-gray-50 p-2 rounded border">
                      {formData.inspection_date ? 
                        new Date(formData.inspection_date).toLocaleString() : 
                        'Not set'}
                    </p>
                  )}
                </div>
              </div>

              {/* Custom Template Fields */}
              {template && template.fields && template.fields.length > 0 ? (
                <>
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-900">Custom Fields</p>
                    <p className="text-xs text-gray-700 mt-1">Specific to this Checklist type</p>
                  </div>
                  <DynamicForm
                    fields={template.fields}
                    formData={formData}
                    setFormData={setFormData}
                    editing={editing}
                  />
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileCheck size={48} className="mx-auto text-gray-400 mb-3 opacity-50" />
                  <p>No custom fields configured</p>
                  <p className="text-xs mt-1">Click &quot;Configure Template&quot; to add custom fields</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Attachments Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-gray-900">Attachments</CardTitle>
              <CardDescription className="text-gray-700">
                Supporting documents and images
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sheet.attachments && sheet.attachments.length > 0 ? (
                <div className="space-y-2">
                  {sheet.attachments.map((attachment, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 border border-gray-300 rounded bg-gray-50">
                      <FileText size={16} className="text-gray-600" />
                      <span className="text-sm text-gray-900">{attachment}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-700 text-center py-4">
                  No attachments
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base text-gray-900">Metadata</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <User size={16} className="text-gray-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-gray-700 font-semibold">Created By</p>
                  <p className="font-medium text-gray-900">{sheet.created_by_name}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calendar size={16} className="text-gray-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-gray-700 font-semibold">Created</p>
                  <p className="font-medium text-gray-900">
                    {new Date(sheet.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calendar size={16} className="text-gray-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-gray-700 font-semibold">Last Updated</p>
                  <p className="font-medium text-gray-900">
                    {new Date(sheet.updated_at).toLocaleString()}
                  </p>
                </div>
              </div>
              {sheet.approved_at && sheet.approved_by_name && (
                <>
                  <div className="border-t pt-3" />
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-gray-700 font-semibold">Approved By</p>
                      <p className="font-medium text-gray-900">{sheet.approved_by_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar size={16} className="text-gray-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-gray-700 font-semibold">Approved On</p>
                      <p className="font-medium text-gray-900">
                        {new Date(sheet.approved_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Project Link */}
          {sheet.project_id && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Project</CardTitle>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(`/projects/${sheet.project_id}`)}
                >
                  <Hash size={14} className="mr-2" />
                  View Project
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base text-gray-900">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {sheet.status !== 'approved' && (
                <>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleStatusChange('under_review')}
                    disabled={sheet.status === 'under_review'}
                  >
                    Submit for Review
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleStatusChange('approved')}
                    disabled={sheet.status === 'approved'}
                  >
                    <CheckCircle size={14} className="mr-2" />
                    Approve Sheet
                  </Button>
                </>
              )}
              {sheet.status === 'under_review' && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleStatusChange('rejected')}
                >
                  <XCircle size={14} className="mr-2" />
                  Reject Sheet
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Dynamic Form Component
const DynamicForm = ({ fields, formData, setFormData, editing }) => {
  const handleFieldChange = (fieldLabel, value) => {
    setFormData({ ...formData, [fieldLabel]: value });
  };

  return (
    <div className="space-y-6">
      {fields.map((field, index) => (
        <DynamicField
          key={index}
          field={field}
          value={formData[field.label]}
          onChange={(value) => handleFieldChange(field.label, value)}
          editing={editing}
        />
      ))}
      {fields.length === 0 && (
        <p className="text-center text-muted-foreground py-4">
          No fields configured for this template
        </p>
      )}
    </div>
  );
};

// Dynamic Field Component
const DynamicField = ({ field, value, onChange, editing }) => {
  switch (field.type) {
    case 'section_header':
      return (
        <div className="pt-4 pb-2 border-b-2 border-gray-300">
          <h3 className="text-xl font-bold text-gray-900">{field.label}</h3>
          {field.description && (
            <p className="text-sm text-gray-600 mt-1">{field.description}</p>
          )}
        </div>
      );
    
    case 'text':
      return (
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-900">
            {field.label}
            {field.required && <span className="text-red-600 ml-1">*</span>}
          </Label>
          {field.description && (
            <p className="text-xs text-gray-600">{field.description}</p>
          )}
          {editing ? (
            <Input
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={`Enter ${field.label.toLowerCase()}...`}
              required={field.required}
              className="border-gray-300 text-gray-900"
            />
          ) : (
            <p className="text-sm text-gray-800 bg-gray-50 p-2 rounded border">{value || '-'}</p>
          )}
        </div>
      );
    
    case 'textarea':
      return (
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-900">
            {field.label}
            {field.required && <span className="text-red-600 ml-1">*</span>}
          </Label>
          {field.description && (
            <p className="text-xs text-gray-600">{field.description}</p>
          )}
          {editing ? (
            <Textarea
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={`Enter ${field.label.toLowerCase()}...`}
              rows={4}
              required={field.required}
              className="border-gray-300 text-gray-900"
            />
          ) : (
            <p className="text-sm text-gray-800 bg-gray-50 p-3 rounded border whitespace-pre-wrap">{value || '-'}</p>
          )}
        </div>
      );
    
    case 'yes_no':
      return (
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-900">
            {field.label}
            {field.required && <span className="text-red-600 ml-1">*</span>}
          </Label>
          {field.description && (
            <p className="text-xs text-gray-600">{field.description}</p>
          )}
          {editing ? (
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer text-gray-900">
                <input
                  type="radio"
                  name={field.label}
                  value="yes"
                  checked={value === 'yes'}
                  onChange={(e) => onChange(e.target.value)}
                  required={field.required}
                  className="w-4 h-4"
                />
                <span className="font-medium">Yes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-gray-900">
                <input
                  type="radio"
                  name={field.label}
                  value="no"
                  checked={value === 'no'}
                  onChange={(e) => onChange(e.target.value)}
                  required={field.required}
                  className="w-4 h-4"
                />
                <span className="font-medium">No</span>
              </label>
            </div>
          ) : (
            <p className="text-sm font-semibold text-gray-800 bg-gray-50 p-2 rounded border">
              {value === 'yes' ? (
                <span className="text-green-700">✓ Yes</span>
              ) : value === 'no' ? (
                <span className="text-red-700">✗ No</span>
              ) : '-'}
            </p>
          )}
        </div>
      );
    
    case 'checkbox':
      return (
        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded border">
          {editing ? (
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => onChange(e.target.checked)}
              className="mt-1 w-4 h-4"
              required={field.required}
            />
          ) : null}
          <div className="flex-1">
            <Label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              {!editing && (value === true || value === 'true') && (
                <span className="text-green-700 font-bold">✓</span>
              )}
              {!editing && !(value === true || value === 'true') && (
                <span className="text-gray-400 font-bold">☐</span>
              )}
              {field.label}
              {field.required && <span className="text-red-600 ml-1">*</span>}
            </Label>
            {field.description && (
              <p className="text-xs text-gray-600 mt-1">{field.description}</p>
            )}
          </div>
        </div>
      );
    
    case 'number':
      return (
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-900">
            {field.label}
            {field.required && <span className="text-red-600 ml-1">*</span>}
          </Label>
          {field.description && (
            <p className="text-xs text-gray-600">{field.description}</p>
          )}
          {editing ? (
            <Input
              type="number"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="0"
              required={field.required}
              className="border-gray-300 text-gray-900"
            />
          ) : (
            <p className="text-sm text-gray-800 bg-gray-50 p-2 rounded border font-mono">{value || '-'}</p>
          )}
        </div>
      );
    
    case 'date':
      return (
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-900">
            {field.label}
            {field.required && <span className="text-red-600 ml-1">*</span>}
          </Label>
          {field.description && (
            <p className="text-xs text-gray-600">{field.description}</p>
          )}
          {editing ? (
            <Input
              type="date"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              required={field.required}
              className="border-gray-300 text-gray-900"
            />
          ) : (
            <p className="text-sm text-gray-800 bg-gray-50 p-2 rounded border">
              {value ? new Date(value).toLocaleDateString() : '-'}
            </p>
          )}
        </div>
      );
    
    case 'dropdown':
      return (
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-900">
            {field.label}
            {field.required && <span className="text-red-600 ml-1">*</span>}
          </Label>
          {field.description && (
            <p className="text-xs text-gray-600">{field.description}</p>
          )}
          {editing ? (
            <Select value={value || ''} onValueChange={onChange}>
              <SelectTrigger className="border-gray-300 text-gray-900">
                <SelectValue placeholder="Select an option..." />
              </SelectTrigger>
              <SelectContent>
                {field.options && field.options.map((option, idx) => (
                  <SelectItem key={idx} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm text-gray-800 bg-gray-50 p-2 rounded border">{value || '-'}</p>
          )}
        </div>
      );
    
    default:
      return null;
  }
};

export default ChecklistDetail;
